import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { useOpinar, useReportarProblema, type CursoDetalle } from './api';

/**
 * Dialogos.tsx — opinar sobre un curso y reportar un problema.
 *
 * ## Sin estado que sobreviva al cierre
 *
 * El vanilla guardaba la calificación y el archivo de evidencia en un objeto del
 * módulo que **sobrevivía al cierre del diálogo**: la nota de una opinión
 * descartada se heredaba en la siguiente, y un archivo elegido y abandonado
 * viajaba en el próximo reporte. Hasta el 2026-08-28 esas dos cosas vivían en
 * `window._fbRating` y `window._reportFile`. Aquí cada apertura empieza de cero.
 */

/* ── Opinar ────────────────────────────────────────────────────────────── */

const ETIQUETAS = ['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente'];

export function Opinion({
  curso, abierto, onCerrar,
}: {
  curso: CursoDetalle;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const opinar = useOpinar();
  const [nota, setNota] = useState(0);
  const [comentario, setComentario] = useState('');
  const [alCorreo, setAlCorreo] = useState(true);
  const [alMuro, setAlMuro] = useState(false);
  const estrellas = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!abierto) return;
    setNota(0); setComentario(''); setAlCorreo(true); setAlMuro(false);
  }, [abierto]);

  function elegir(n: number) {
    setNota(n);
    estrellas.current[n - 1]?.focus();
  }

  function enviar() {
    if (!nota) {
      Utils.toast('Elige una calificación antes de enviar', 'warning');
      estrellas.current[0]?.focus();
      return;
    }
    opinar.mutate(
      {
        cursoId: curso.id,
        rating: nota,
        comment: comentario.trim() || null,
        publish_to_feed: alMuro,
        send_to_email: alCorreo,
      },
      {
        onSuccess: (r) => {
          Utils.toast(r.message || 'Gracias por tu opinión', 'success');
          onCerrar();
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar tu opinión', 'error'),
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Opinar sobre el curso"
      descripcion={`Tu opinión sobre «${curso.title}» ayuda a mejorar el contenido.`}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={opinar.isPending}>
            <i className="bi bi-send" aria-hidden="true" /> Enviar opinión
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <span id="op-nota" className="mb-1.5 block text-2 font-semibold text-ink-2">Calificación</span>
          <div className="flex items-center gap-3">
            {/* Un grupo de opciones de verdad: una sola parada de Tab, las
                flechas mueven la nota y el lector anuncia «3 estrellas,
                marcado». El vanilla ya lo había corregido; se conserva. */}
            <div
              role="radiogroup"
              aria-labelledby="op-nota"
              className="flex gap-1"
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); elegir(Math.min(5, (nota || 0) + 1)); }
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); elegir(Math.max(1, (nota || 2) - 1)); }
              }}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  ref={(el) => { estrellas.current[n - 1] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={nota === n}
                  aria-label={`${n} estrella${n === 1 ? '' : 's'}`}
                  tabIndex={nota === n || (!nota && n === 1) ? 0 : -1}
                  onClick={() => setNota(n)}
                  className={cn(
                    'rounded-xs p-0.5 text-7 leading-none focus-visible:focus-anillo',
                    n <= nota ? 'text-warn-ink' : 'text-muted',
                  )}
                >
                  <i className={cn('bi', n <= nota ? 'bi-star-fill' : 'bi-star')} aria-hidden="true" />
                </button>
              ))}
            </div>
            <span className="text-2 text-muted" aria-live="polite">{ETIQUETAS[nota]}</span>
          </div>
        </div>

        <Field label="Comentario" opcional>
          <Textarea
            rows={3}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="¿Qué te pareció el curso?"
          />
        </Field>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-2 font-semibold text-ink-2">¿Cómo quieres compartirla?</legend>
          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
            <input
              type="checkbox"
              checked={alCorreo}
              onChange={(e) => setAlCorreo(e.target.checked)}
              className="size-4 accent-brand"
            />
            <i className="bi bi-envelope" aria-hidden="true" /> Enviarla por correo a Bienestar
          </label>
          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
            <input
              type="checkbox"
              checked={alMuro}
              onChange={(e) => setAlMuro(e.target.checked)}
              className="size-4 accent-brand"
            />
            <i className="bi bi-chat-square-text" aria-hidden="true" /> Publicarla en el muro
          </label>
        </fieldset>
      </div>
    </Dialog>
  );
}

/* ── Reportar un problema ──────────────────────────────────────────────── */

/** El límite que aplica el servidor a la evidencia. */
const MAX_EVIDENCIA_MB = 10;

export function ReportarProblema({
  curso, leccionId, abierto, onCerrar,
}: {
  curso: CursoDetalle;
  leccionId: number | null;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const reportar = useReportarProblema();
  const [descripcion, setDescripcion] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!abierto) return;
    setDescripcion(''); setArchivo(null); setError('');
  }, [abierto]);

  const leccion = leccionId ? curso.lessons.find((l) => l.id === leccionId) : null;

  function elegirArchivo(entrada: HTMLInputElement) {
    const f = entrada.files?.[0];
    if (!f) { setArchivo(null); return; }
    if (f.size > MAX_EVIDENCIA_MB * 1024 * 1024) {
      Utils.toast(`«${f.name}» pesa más de ${MAX_EVIDENCIA_MB} MB`, 'warning');
      // Fuera también la anterior: el campo enseñaría la rechazada y el reporte
      // saldría con la de antes.
      setArchivo(null);
      entrada.value = '';
      return;
    }
    setArchivo(f);
  }

  function enviar() {
    const limpio = descripcion.trim();
    if (!limpio) {
      setError('Describe el problema para que se pueda arreglar.');
      return;
    }
    reportar.mutate(
      { cursoId: curso.id, descripcion: limpio, leccionId, archivo },
      {
        onSuccess: () => {
          Utils.toast('Reporte enviado. Gracias por avisar.', 'success');
          onCerrar();
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar el reporte', 'error'),
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Reportar un problema"
      descripcion={leccion ? `En la lección «${leccion.title}».` : `En el curso «${curso.title}».`}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={reportar.isPending}>
            <i className="bi bi-send" aria-hidden="true" /> Enviar reporte
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Qué pasa" error={error}>
          <Textarea
            rows={4}
            value={descripcion}
            onChange={(e) => { setDescripcion(e.target.value); setError(''); }}
            placeholder="El vídeo no carga, el PDF está en blanco, una pregunta del quiz está mal…"
          />
        </Field>
        <Field label="Evidencia" opcional ayuda={`Una imagen o un PDF, hasta ${MAX_EVIDENCIA_MB} MB.`}>
          <Input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => elegirArchivo(e.currentTarget)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
